import { useMutation } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import * as Yup from "yup";
import TableUI from "../../../../../components/Table";
import { Button, Input, Modal, Pagination } from "../../../../../components/ui";
import {
  CREATE_WAREHOUSE_LOCATION,
  UPDATE_WAREHOUSE_LOCATION,
} from "../../../../../graphql/mutation/WarehouseSite";
import {
  LOCATIONS_PAGE_SIZE,
  filterSiteLocations,
  getLocationTypeOptions,
  getMutationError,
  getSiteViewingRoomOptions,
  toLocationRow,
  toLocationUpdateInput,
} from "../helpers";
import AddRecordForm from "./AddRecordForm";
import Panel from "./Panel";

const SITE_REFETCH_QUERIES = ["FetchSiteLocations", "FetchSiteViewingRooms"];

const addLocationSchema = Yup.object({
  capacity: Yup.number().nullable().min(0, "Capacity cannot be negative"),
  description: Yup.string().trim().required("Description is required"),
});

const getLocationHeaders = ({ locationTypes, siteId, viewingRooms }) => [
  {
    label: "Description",
    name: "description",
    type: "input",
    inputType: "text",
  },
  ({ formik }) => ({
    label: "Type",
    name: "typeId",
    type: "input",
    inputType: "multi-select",
    isNullable: true,
    placeholder: "No type",
    options: getLocationTypeOptions({
      locationTypes,
      currentType: formik?.values?.type,
    }),
  }),
  ({ formik }) => ({
    label: "Viewing room",
    name: "viewingRoomId",
    type: "input",
    inputType: "multi-select",
    isNullable: true,
    placeholder: "No viewing room",
    options: getSiteViewingRoomOptions({
      viewingRooms,
      siteId,
      currentViewingRoom: formik?.values?.viewingRoom,
    }),
  }),
  {
    label: "Capacity",
    name: "capacity",
    type: "input",
    inputType: "number",
    allowEmpty: true,
    width: "120px",
  },
];

const LocationsPanel = props => {
  const { loading, locationTypes, locations, siteId, viewingRooms } =
    props || {};
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);

  const [updateWarehouseLocation] = useMutation(UPDATE_WAREHOUSE_LOCATION, {
    refetchQueries: SITE_REFETCH_QUERIES,
  });
  const [createWarehouseLocation, { loading: creating }] = useMutation(
    CREATE_WAREHOUSE_LOCATION,
    { refetchQueries: SITE_REFETCH_QUERIES },
  );

  const filteredLocations = filterSiteLocations({ locations, search });
  const rows = filteredLocations
    .slice(offset, offset + LOCATIONS_PAGE_SIZE)
    .map(toLocationRow);

  // Table rows re-send pending edits whenever these identities change.
  const headers = useMemo(
    () => getLocationHeaders({ locationTypes, siteId, viewingRooms }),
    [locationTypes, siteId, viewingRooms],
  );
  const updateLocation = useCallback(
    async ({ variables }) => {
      const result = await updateWarehouseLocation({
        variables: { input: toLocationUpdateInput(variables?.input) },
      });
      setError(getMutationError(result, "updateWarehouseLocation"));
      return result;
    },
    [updateWarehouseLocation],
  );
  const setMutationErrorMessage = useCallback(
    mutationError => setError(mutationError?.message),
    [],
  );

  const createLocation = async values => {
    const result = await createWarehouseLocation({
      variables: {
        input: {
          capacity:
            values.capacity === "" || values.capacity == null
              ? null
              : Number(values.capacity),
          description: values.description,
          siteId,
          typeId: values.typeId ?? null,
          viewingRoomId: values.viewingRoomId ?? null,
        },
      },
    });
    return getMutationError(result, "createWarehouseLocation");
  };

  const addLocationInputs = [
    { label: "Description", name: "description", type: "text" },
    {
      label: "Type",
      name: "typeId",
      type: "multi-select",
      isNullable: true,
      options: getLocationTypeOptions({ locationTypes }),
    },
    {
      label: "Viewing room",
      name: "viewingRoomId",
      type: "multi-select",
      isNullable: true,
      options: getSiteViewingRoomOptions({ viewingRooms, siteId }),
    },
    { label: "Capacity", name: "capacity", type: "number", allowEmpty: true },
  ];

  const addLocationModalProps = {
    body: ({ closeModal }) => (
      <AddRecordForm
        closeModal={closeModal}
        initialValues={{
          capacity: null,
          description: "",
          typeId: null,
          viewingRoomId: null,
        }}
        inputs={addLocationInputs}
        loading={creating}
        onSubmit={createLocation}
        title="Add location"
        validationSchema={addLocationSchema}
      />
    ),
    closeOnBackdrop: true,
    hideCloseButton: true,
    scale: "md",
  };

  const actions = (
    <>
      <div className="w-full sm:w-72">
        <Input
          name="locationSearch"
          placeholder="Search locations"
          type="text"
          value={search}
          onChange={event => {
            setSearch(event.target.value);
            setOffset(0);
          }}
        />
      </div>
      <Modal {...addLocationModalProps}>
        <Button label="Add location" action="black" />
      </Modal>
    </>
  );

  return (
    <Panel title="Locations" actions={actions} error={error}>
      <div className="overflow-x-auto">
        <TableUI
          headers={headers}
          loading={loading}
          rows={rows}
          updateMutation={updateLocation}
          variant="dynamicHeight"
          onMutationError={setMutationErrorMessage}
        />
      </div>
      <div className="pt-6">
        <Pagination
          count={filteredLocations.length}
          limit={LOCATIONS_PAGE_SIZE}
          offset={offset}
          onChange={({ offset: nextOffset }) => setOffset(nextOffset)}
        />
      </div>
    </Panel>
  );
};

export default LocationsPanel;
